import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { TodoTasksBoard } from '@demind-inc/core';

import {
  getOAuthAccessToken,
  integrationsApi,
  tasksApi,
  useInitTodoIntegration,
} from '../../../data-access';
import './ClickUpAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';

interface ExchangeCodeRes {
  accessToken: string;
}

interface ErrorState {
  message: string;
  fatal: boolean;
}

const ClickUpAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initTodoIntegration } = useInitTodoIntegration();
  const hasExchanged = useRef(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [redirectDestination, setRedirectDestination] = useState<string>(Path.DASHBOARD);

  const [taskLists, setTaskLists] = useState<Partial<TodoTasksBoard>[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());

  const [isExchangeInFlight, setIsExchangeInFlight] = useState(false);
  const [isFetchingTaskLists, setIsFetchingTaskLists] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState | null>(null);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        navigate(Path.DASHBOARD);
        return;
      }

      if (hasExchanged.current) return;
      hasExchanged.current = true;

      setIsExchangeInFlight(true);
      setErrorState(null);

      let redirectTo = searchParams.get('redirectTo') ?? Path.DASHBOARD;
      let userId = searchParams.get('userId');
      let isMobileApp = searchParams.get('isMobileApp')?.includes('true');

      if (state) {
        try {
          const params = new URLSearchParams(new URL(state).search);
          redirectTo = params.get('redirectTo') ?? redirectTo;
          userId = params.get('userId') ?? userId;
          isMobileApp = params.get('isMobileApp')?.includes('true') ?? isMobileApp;
        } catch (error) {
          console.error('Failed to parse ClickUp auth state', error);
        }
      }

      try {
        setRedirectDestination(redirectTo);
        setUserId(userId || '');
        setIsMobileApp(isMobileApp ?? false);

        if (!userId) {
          console.error('No userId found in ClickUp redirect URL');
          navigate(redirectTo);
          return;
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'clickup',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.CLICKUP_CALLBACK, searchParams)
        );

        if (!data) {
          throw new Error('No access token returned.');
        }

        const accessToken = (data as ExchangeCodeRes).accessToken;
        if (!accessToken) {
          throw new Error('No access token returned.');
        }

        setAccessToken(accessToken);
      } catch (error) {
        console.error('Error while exchanging ClickUp code', error);
        setErrorState({
          message: "Couldn't connect to ClickUp — try again?",
          fatal: true,
        });
        hasExchanged.current = false;
      } finally {
        setIsExchangeInFlight(false);
      }
    };

    exchange();
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let isActive = true;
    setIsFetchingTaskLists(true);

    integrationsApi
      .getOriginalTodoBoards('clickup', accessToken)
      .then(({ data }) => {
        if (!isActive) return;
        const fetchedTaskLists = data as Partial<TodoTasksBoard>[];
        setTaskLists(fetchedTaskLists);
        setSelectedListIds(
          new Set(
            fetchedTaskLists
              .filter((taskList) => !taskList.closed)
              .map((taskList) => taskList.originalBoardId)
              .filter(Boolean) as string[]
          )
        );

        if (!userId) {
          return;
        }

        if (!getOAuthAccessToken()) {
          return [fetchedTaskLists, null] as const;
        }

        return tasksApi
          .getTodoProjects(userId)
          .then((response) => [fetchedTaskLists, response] as const)
          .catch((error) => {
            console.warn('Failed to fetch existing ClickUp projects', error);
            return [fetchedTaskLists, null] as const;
          });
      })
      .then((result) => {
        if (!isActive || !result) return;
        const [fetchedTaskLists, response] = result;
        const projects = (response?.data as TodoTasksBoard[]) || [];
        const clickupProjects = projects.filter(
          (project) => project.appFrom === 'clickup' && !project.closed
        );
        const existingTaskListIds = new Set(
          clickupProjects.map((project) => project.originalBoardId).filter(Boolean) as string[]
        );

        const listsToSelect = fetchedTaskLists
          .map((list) => list.originalBoardId)
          .filter((id) => id && existingTaskListIds.has(id)) as string[];

        if (listsToSelect.length > 0) {
          setSelectedListIds(new Set(listsToSelect));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch ClickUp lists', error);
        if (!isActive) return;
        setErrorState({ message: "Couldn't load your ClickUp lists.", fatal: true });
      })
      .finally(() => {
        if (!isActive) return;
        setIsFetchingTaskLists(false);
      });

    return () => {
      isActive = false;
    };
  }, [accessToken, userId]);

  const handleToggleSelectAll = () => {
    const availableListIds = taskLists
      .map((taskList) => taskList.originalBoardId)
      .filter(Boolean) as string[];

    setSelectedListIds((prev) => {
      if (!availableListIds.length) {
        return prev;
      }

      if (prev.size === availableListIds.length) {
        return new Set();
      }

      return new Set(availableListIds);
    });
  };

  const handleToggleTaskList = (taskListId: string) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);

      if (next.has(taskListId)) {
        next.delete(taskListId);
      } else {
        next.add(taskListId);
      }

      return next;
    });
  };

  const handleSync = async () => {
    if (!userId) {
      setErrorState({ message: "We're missing some account info — try connecting again.", fatal: true });
      return;
    }

    const listIds = Array.from(selectedListIds);

    if (!listIds.length) {
      return;
    }

    if (!accessToken) {
      setErrorState({ message: "We couldn't verify your login — try connecting again.", fatal: true });
      return;
    }

    try {
      setErrorState(null);
      setIsSyncing(true);
      await initTodoIntegration({
        token: accessToken,
        todoType: 'clickup',
        userId,
        shouldSyncTasks: true,
        skipRefresh: isMobileApp,
        originalBoardIds: listIds,
      });

      trackEventMixpanel('connect_todo_auth_done', {
        todoType: 'clickup',
        fromMobileApp: isMobileApp,
        taskListsSelected: listIds.length,
      });

      if (isMobileApp) {
        window.location.href = 'lifestack://todo-callback';
        return;
      }

      navigate(redirectDestination || Path.DASHBOARD);
    } catch (error) {
      console.error('Failed to start ClickUp sync', error);
      setErrorState({
        message: "Couldn't start syncing those lists.",
        fatal: false,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isExchangeInFlight && !accessToken && !errorState) {
    return (
      <div className="clickup-auth-callback">
        <CircularProgress />
      </div>
    );
  }

  const availableListIds = taskLists
    .map((taskList) => taskList.originalBoardId)
    .filter(Boolean) as string[];
  const allSelected =
    availableListIds.length > 0 && selectedListIds.size === availableListIds.length;
  const isIndeterminate = selectedListIds.size > 0 && !allSelected;
  const fatalError = errorState?.fatal;
  const errorMessage = errorState?.message;

  return (
    <div className="clickup-auth-callback">
      <div className="clickup-auth-callback__card">
        <Typography variant="h5" component="h1" className="clickup-auth-callback__title">
          Select ClickUp lists
        </Typography>

        {fatalError ? (
          <div className="clickup-auth-callback__error">
            <Typography color="error" variant="body2" className="clickup-auth-callback__error-text">
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(redirectDestination || Path.DASHBOARD)}
            >
              Back to dashboard
            </Button>
          </div>
        ) : (
          <div className="clickup-auth-callback__body">
            {errorMessage ? (
              <Typography
                color="error"
                variant="body2"
                className="clickup-auth-callback__error-text"
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" className="clickup-auth-callback__description">
              Choose the lists you want Lifestack to import tasks from.
            </Typography>

            {taskLists.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                  />
                }
                label="Select all lists"
                className="clickup-auth-callback__select-all"
              />
            )}

            <div className="clickup-auth-callback__list">
              {isFetchingTaskLists ? (
                <div className="clickup-auth-callback__loading">
                  <CircularProgress size={24} />
                </div>
              ) : taskLists.length ? (
                taskLists.map((taskList) => {
                  const taskListId = taskList.originalBoardId;
                  if (!taskListId) {
                    return null;
                  }

                  const isChecked = selectedListIds.has(taskListId);

                  return (
                    <label
                      key={taskListId}
                      className="clickup-auth-callback__list-item"
                      htmlFor={`task-list-${taskListId}`}
                    >
                      <Checkbox
                        id={`task-list-${taskListId}`}
                        checked={isChecked}
                        onChange={() => handleToggleTaskList(taskListId)}
                      />
                      <div className="clickup-auth-callback__list-item-info">
                        <span className="clickup-auth-callback__list-item-name">
                          {taskList.name || 'Untitled list'}
                        </span>
                      </div>
                    </label>
                  );
                })
              ) : (
                <Typography variant="body2" className="clickup-auth-callback__empty">
                  No lists found in this ClickUp workspace.
                </Typography>
              )}
            </div>

            <div className="clickup-auth-callback__actions">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={selectedListIds.size === 0 || isSyncing || !!fatalError}
                className="clickup-auth-callback__sync-button"
              >
                {isSyncing ? (
                  <>
                    <CircularProgress size={18} className="clickup-auth-callback__button-spinner" />
                    Syncing lists
                  </>
                ) : (
                  'Import selected lists'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClickUpAuthCallback;
